import { ReactNode, useState } from "react";
import { useIonRouter } from "@ionic/react";
import {
  Chip,
  type ChipCancelledStatus,
  type ChipClosedStatus,
  type ChipCompletedStatus,
  type ChipFilledDisplay,
  type ChipPendingStatus,
  type ChipUnfilledStatus,
  type ChipWaitlistStatus,
} from "components";
import { RequestModel, RequestStatus } from "model";
import { Link } from "react-router-dom";
import { createRequestASubPath } from "routes/routeData";
import { Alert, Badge, Card, Colors, DataItemView, Icon } from "swing-components";

import { useAllDatesTimes } from "~components/AllDatesTimes";
import { useCancelRequest } from "~components/CancelRequest";
import { EditRequestModal } from "~components/EditRequest/EditRequestModal";
import { RESTRICTIONS, translate, useShouldRestrictAdmin } from "~utils";

import { GiveFeedback } from "../AdminExperience/GiveFeedback/GiveFeedback";
import styles from "./RequestCard.module.css";

/***** Types *****/
type FilledStatus = {
  status: "FILLED";
  sub: {
    profileImageUrl?: string;
    name?: string;
    onSubNameClick?: () => void;
  };
  display: ChipFilledDisplay;
};

type UnfilledStatus = ChipUnfilledStatus;
type CompletedStatus = ChipCompletedStatus;
type CancelledStatus = ChipCancelledStatus;
type WaitlistStatus = ChipWaitlistStatus;
type PendingStatus = ChipPendingStatus;
type ClosedStatus = ChipClosedStatus;

type RequestDetailProps = {
  request: RequestModel;
};

type RequestCardProps = (
  | FilledStatus
  | UnfilledStatus
  | CompletedStatus
  | CancelledStatus
  | WaitlistStatus
  | PendingStatus
  | ClosedStatus
) &
  RequestDetailProps;

export function RequestCard(props: RequestCardProps) {
  const router = useIonRouter();

  // Placed here to only run once
  // We can move this to the header component once
  // the feature flag is removed and update the props

  const handleNavigateToRequestDetails = () => {
    const path = `${router?.routeInfo?.pathname}/${props.request.id}`;
    router.push(path);
  };

  return (
    <Card onClick={() => {}}>
      {/* Only the header and body of the card should be clickable and navigate to the request details page */}
      {/* This container includes nested elements that render buttons.
       * Wrapping everything in an actual <button> violates valid HTML structure,
       * so using a <div> with role="button" here instead.
       */}
      <div onClick={handleNavigateToRequestDetails} role="button">
        {/* Header */}
        <RequestCardHeader {...props} />
        {/* Body */}
        <RequestCardBody {...props} />
      </div>
      {/* Footer */}
      <RequestCardFooter {...props} />
    </Card>
  );
}

/********** Header **********/
function RequestCardHeader(props: RequestCardProps) {
  const { request, status } = props;
  const shouldHideFeedback = useShouldRestrictAdmin([RESTRICTIONS.HIDE_SUB_FEEDBACK]);

  const { openAllDatesTimesModal } = useAllDatesTimes(request);

  // Used to drive optimistic UI updates and layout:
  // `undefined` = untouched,
  // `false` = successfully submitted (update alignment early),
  // `true` = submission failed, show error state
  const [hasFeedbackSubmissionError, setHasFeedbackSubmissionError] = useState<
    boolean | undefined
  >();

  // Determine whether the quick review prompt should be shown.
  // Conditions:
  // - Feature flag for sub ratings is enabled
  // - Admin is allowed to view previous reviews/give reviews
  // - A sub is assigned (sub.name is present)
  // - The request is in the past
  // - If the request was canceled, only allow review to be given if the sub worked before cancellation
  const shouldShowReviewPrompt =
    !shouldHideFeedback &&
    request.sub.name &&
    request.isPast() &&
    (!request.isCanceled() || request.didSubCompleteWorkBeforeCancel());

  return (
    <div css={{ display: "flex", flexDirection: "column", alignItems: "start", gap: 8 }}>
      {/* Error message for repeat review submission */}
      {hasFeedbackSubmissionError && (
        <div css={{ width: "100%" }}>
          <Alert showIcon color="danger">
            <p
              css={{ margin: 0 }}
              dangerouslySetInnerHTML={{
                __html: translate("GIVE_FEEDBACK_REPEAT_REVIEW_SUBMISSION_ERROR_MSG"),
              }}
            />
          </Alert>
        </div>
      )}

      <Chip {...props} />

      {/* Review Prompt */}
      {shouldShowReviewPrompt && (
        <div
          css={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems:
              // Prevent layout jump by aligning early on optimistic success
              request.hasFeedback || hasFeedbackSubmissionError === false
                ? "flex-start"
                : "stretch",
            width: "100%",
            paddingBottom: 16,
          }}
          // If the request doesn't have feedback yet, stop click propagation
          // to allow the quick review prompt to be interactive.
          // If feedback already exists, allow the click to bubble up
          // so the admin can navigate to the request details page.
          {...(!request.hasFeedback && { onClick: (e) => e.stopPropagation() })}
        >
          <GiveFeedback
            school={request.school}
            subName={request.sub.name}
            requestId={request.id}
            subId={request.sub.id}
            status={status}
            intervals={request.getIntervals(true)}
            subProfileImgUrl={request.sub.photo}
            initiationType="START_WITH_RATING"
            subLastFeedback={request.subLastFeedback}
            feedback={request.feedback}
            hasFeedback={request.hasFeedback}
            hasFeedbackSubmissionError={hasFeedbackSubmissionError}
            setHasSubmissionError={setHasFeedbackSubmissionError}
          />

          {/* Visual divider to avoid extra pixels */}
          <div
            css={{
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 1,
              backgroundColor: Colors.slate200,
              pointerEvents: "none",
            }}
          />
        </div>
      )}

      {/* Date and time */}
      <div css={{ marginTop: 8 }}>
        <DataItemView
          stackLayout
          icon={request.isMultiDayRequest() ? "Calendar Overlap" : "Calendar Today"}
          label={
            <div css={{ marginLeft: -8, marginBottom: 6 }}>
              {request.status === RequestStatus.Submitted && request.recentlyTrimmed && (
                <div css={{ marginBottom: 4 }}>
                  <Badge status="tertiary" text={translate("LABEL_AUTO_ADJUSTED_START_DATE")} />
                </div>
              )}
              <h3 css={{ margin: 0 }}>{request.clientDate}</h3>
            </div>
          }
        >
          {!request.isMultiDayRequest() && (
            <p css={{ margin: 0, marginLeft: -8, marginBottom: 0, fontSize: 16 }}>
              {request.clientTime}
            </p>
          )}
          {request.isMultiDayRequest() && (
            <a
              onClick={(e) => {
                e.stopPropagation();
                openAllDatesTimesModal();
              }}
              css={{ background: "none", color: Colors.blue500, display: "inline-block" }}
            >
              <p css={{ margin: 0, marginLeft: -8, marginBottom: 0, fontSize: 16 }}>
                {translate("MULTI_DAY_ALL_DATES_TIMES_LABEL")}
              </p>
            </a>
          )}

          {request?.isLTRequestPending() && (
            <PendingIndicator>
              <p css={{ margin: 0 }}>{translate("LABEL_PENDING_ADJUSTMENTS")}</p>
            </PendingIndicator>
          )}
        </DataItemView>
      </div>
      <div css={{ height: "1px", width: "100%", backgroundColor: Colors.slate200 }} />
    </div>
  );
}

/********** Body **********/
function RequestCardBody(props: RequestDetailProps) {
  const { request } = props;

  const gradeDisplay = request.getGradeDisplay();
  const subjectDisplay = request.getSubjectDisplay({ showUnknownAsNote: true });
  const gradeAndSubjectDisplay = getGradeAndSubjectDisplay(gradeDisplay, subjectDisplay);

  return (
    <>
      <div css={{ padding: "8px 0" }}>
        <DataItemView
          stackLayout
          icon="Location"
          css={{
            padding: "8px 0",
          }}
          label={
            <p css={{ margin: 0, fontSize: 16, fontWeight: 400 }}>
              {request.school.name || translate("NOT_PROVIDED")}{" "}
            </p>
          }
        />
      </div>
      <div css={{ padding: "8px 0" }}>
        <DataItemView
          stackLayout
          icon="Teacher"
          css={{
            padding: "8px 0",
          }}
          label={
            <p css={{ margin: 0, fontSize: 16, fontWeight: 400 }}>
              {request.getTeacherDisplay() || translate("NOT_PROVIDED")}
            </p>
          }
        />
      </div>
      <div css={{ padding: "8px 0" }}>
        <DataItemView
          stackLayout
          icon="Book"
          css={{
            padding: "8px 0",
          }}
          label={<p css={{ margin: 0, fontSize: 16, fontWeight: 400 }}>{gradeAndSubjectDisplay}</p>}
        />
      </div>
      <div css={{ height: "1px", width: "100%", backgroundColor: Colors.slate200 }} />
    </>
  );
}

/********** Footer **********/
function RequestCardFooter(props: RequestCardProps) {
  const { request } = props;

  /*** Hooks ***/
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const viewOnly = useShouldRestrictAdmin([
    RESTRICTIONS.VIEW_ONLY_ADMIN,
    RESTRICTIONS.CLASSROOM_VIEW_ONLY,
  ]);

  const { openCancelRequestModal } = useCancelRequest(request);

  /*** Constants ****/
  const canModify = !request.isPast() && !viewOnly && !request.isCanceled();
  const editRequestModalTrigger = `edit-request-modal-request-${request.id}`;

  /*** Render ****/
  return (
    <footer
      css={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-evenly",
        gap: 16,
        paddingTop: 16,
        fontWeight: 600,
        fontSize: 16,
      }}
    >
      {/* Edit */}
      {canModify && (
        <button
          id={editRequestModalTrigger}
          className={styles.footerButton}
          onClick={(e) => {
            setIsEditModalOpen(true);
            e.stopPropagation();
          }}
        >
          <p css={{ margin: 0 }}>{translate("LABEL_EDIT")}</p>
        </button>
      )}
      {/*
        Conditionally rendering this modal to reduce fetching data we don't
        need on render.
      */}
      {isEditModalOpen && (
        <EditRequestModal request={request} onClose={() => setIsEditModalOpen(false)} />
      )}

      {/* Duplicate */}
      <Link
        to={`${createRequestASubPath()}?clonedFrom=${request.id}`}
        css={{ textDecoration: "none", padding: "8px 16px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <p css={{ margin: 0 }}>{translate("REQUEST_CARD_LABEL_DUPLICATE")}</p>
      </Link>

      {canModify && (
        <button
          className={styles.footerButton}
          onClick={(e) => {
            e.stopPropagation();
            openCancelRequestModal();
          }}
        >
          <p css={{ margin: 0 }}>{translate("LABEL_CANCEL")}</p>
        </button>
      )}
    </footer>
  );
}
/********** Helper Components **********/
export function PendingIndicator(props: { children: ReactNode }) {
  const { children } = props;

  return (
    <div className={styles.pendingIndicator}>
      <Icon name="Alert" color={Colors.yellow600} />
      {children}
    </div>
  );
}

/********** Helper Functions **********/
/*
 * Conditionally display Grade and subject before the grade and subject lists if one or the other is Unknown or Not provided
 */
const getGradeAndSubjectDisplay = (gradeDisplay: string, subjectDisplay: string) => {
  let tempGradeDisplay = "";
  let tempSubjectDisplay = "";

  switch (gradeDisplay) {
    case subjectDisplay:
      tempGradeDisplay = gradeDisplay;
      break;
    case translate("UNKNOWN_SEE_NOTES"):
    case translate("NOT_PROVIDED"):
      tempGradeDisplay = `${translate("LABEL_GRADE")} ${gradeDisplay}`;
      break;
    default:
      tempGradeDisplay = gradeDisplay;
  }

  switch (subjectDisplay) {
    case gradeDisplay:
      tempSubjectDisplay = subjectDisplay;
      break;
    case translate("UNKNOWN_SEE_NOTES"):
    case translate("NOT_PROVIDED"):
      tempSubjectDisplay = `${translate("LABEL_SUBJECT")} ${subjectDisplay}`;
      break;
    default:
      tempSubjectDisplay = subjectDisplay;
  }

  return tempGradeDisplay === tempSubjectDisplay
    ? gradeDisplay
    : `${tempGradeDisplay}, ${tempSubjectDisplay}`;
};
